import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/client';
import { payCommitment } from '../checkout';

const inr = (v) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const downloadDocument = async (doc) => {
  const res = await api.get(doc.download_url, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.title;
  a.click();
  URL.revokeObjectURL(url);
};

const DealDetail = () => {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(null);
  const [shares, setShares] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/api/spvs/${id}/`).then((r) => setDeal(r.data)).catch((e) => setError(e.response?.status || 0));
  }, [id]);

  const commit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const c = (await api.post('/api/commitments/', { spv: deal.id, shares: Number(shares) })).data;
      try {
        if ((await payCommitment(c.id)) === 'paid') toast.success('Payment received');
      } catch (pe) {
        toast.error(pe.message);
      }
      navigate('/commitments');
    } catch (ce) {
      toast.error(ce.response?.data?.detail || 'Could not create the commitment.');
    } finally {
      setBusy(false);
    }
  };

  if (error === 403) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p role="alert">KYC approval is required to view deals.</p><Link to="/kyc">Complete your KYC</Link></main>;
  if (error) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p role="alert">Deal not found.</p><Link to="/deals">Back to deals</Link></main>;
  if (!deal) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p>Loading…</p></main>;

  const ill = deal.illustration;
  const dev = deal.deal_type === 'development';
  return (
    <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>
      <p><Link to="/deals">← Deals</Link></p>
      <h1>{deal.name}</h1>
      <p>{deal.status} · {deal.deal_type.replace('_', ' ')}{deal.cin && ` · CIN ${deal.cin}`}</p>
      {deal.description && <p>{deal.description}</p>}

      <h2>Offer</h2>
      <dl>
        <dt>Price per share</dt><dd>{inr(deal.share_price)}</dd>
        <dt>Shares offered</dt><dd>{deal.shares_offered.toLocaleString('en-IN')}</dd>
        <dt>Minimum</dt><dd>{deal.min_shares} shares ({inr(deal.minimum_investment)})</dd>
        <dt>Maximum investors</dt><dd>{deal.max_investors}</dd>
      </dl>

      {deal.status === 'raising' && (
        <form onSubmit={commit} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, margin: '12px 0' }}>
          <h2 style={{ marginTop: 0 }}>Invest</h2>
          <label>Number of shares (minimum {deal.min_shares})
            <input type="number" min={deal.min_shares} step="1" value={shares} onChange={(e) => setShares(e.target.value)} className="input" required />
          </label>
          {shares > 0 && <p>Amount: {inr(Number(shares) * Number(deal.share_price))}</p>}
          <p><small>Your commitment is held for a limited time while you pay. Returns are not guaranteed and you may lose money.</small></p>
          <button type="submit" disabled={busy || !shares}>{busy ? 'Please wait…' : 'Commit and pay'}</button>
        </form>
      )}

      <h2>Assets</h2>
      <ul>{deal.assets.map((a) => (
        <li key={a.id}>{a.asset_type.replace('_', ' ')} — {a.location}{a.area_sq_yd && `, ${a.area_sq_yd} sq yd`}
          {a.map_url && <> · <a href={a.map_url} target="_blank" rel="noreferrer">map</a></>}</li>
      ))}</ul>

      <h2>Milestones</h2>
      {deal.milestones.length === 0 ? <p>None yet.</p> : (
        <table><thead><tr><th>Milestone</th><th>Target</th><th>Actual</th><th>Status</th></tr></thead>
          <tbody>{deal.milestones.map((m) => (
            <tr key={m.id}><td>{m.type.replace('_', ' ')}{m.note && ` — ${m.note}`}</td><td>{m.target_date || '-'}</td><td>{m.actual_date || '-'}</td><td>{m.status}</td></tr>
          ))}</tbody></table>
      )}

      <h2>Documents</h2>
      {deal.documents.length === 0 ? <p>None available.</p> : (
        <ul>{deal.documents.map((d) => (
          <li key={d.id}>{d.title} ({d.doc_type}) <button onClick={() => downloadDocument(d)}>Download</button></li>
        ))}</ul>
      )}

      <h2>Fees</h2>
      <p>{deal.fee_rule.text}</p>
      <p>{deal.fee_rule.resale}</p>

      <h2>How the fee rule works: illustration</h2>
      <p><strong>{ill.disclaimer}</strong> {ill.basis} Capital: {inr(ill.capital)}.</p>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr>
            {dev && <th>Hold (years)</th>}<th>Net proceeds ÷ capital</th><th>Net proceeds</th>
            {dev && <th>Investors' XIRR</th>}<th>{dev ? 'Company fee' : 'Exit fee'}</th><th>Distributable</th><th>Per share</th>
          </tr></thead>
          <tbody>{ill.rows.map((r, i) => (
            <tr key={i}>
              {dev && <td>{r.hold_years}</td>}<td>{r.net_multiple}×</td><td>{inr(r.net_proceeds)}</td>
              {dev && <td>{r.investor_xirr_pct}%</td>}<td>{inr(r.company_fee)}</td><td>{inr(r.distributable)}</td><td>{inr(r.per_share)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </main>
  );
};

export default DealDetail;
